/**
 * CampaignExplainabilityCard
 * Explains how Pravik generated the campaign plays
 * 
 * Visual redesign: Clean, neutral info bar with minimal colors
 */

import { useState } from 'react';
import { Badge } from '../../../components/ui/badge';
import { 
  Lightbulb, ChevronDown, ChevronUp, Target, Search, 
  RefreshCw, Smartphone, Mail, Users, Building2
} from 'lucide-react';

// Simplified stage/channel labels (no colored badges in collapsed view)
const STAGE_LABELS = {
  decision: 'Decision',
  consideration: 'Consideration',
  retargeting: 'Retargeting',
  awareness: 'Awareness',
  retention: 'Retention'
};

const CHANNEL_LABELS = {
  search_paid: 'Search',
  meta_paid: 'Meta',
  linkedin_ads: 'LinkedIn',
  retargeting: 'Retargeting',
  email_lifecycle: 'Email',
  partnerships: 'Partners'
};

/**
 * Compute top stages and channels per segment from plays
 */
function computeSegmentInsights(plays, segmentId) {
  const segmentPlays = plays.filter(p => p.segment_id === segmentId);
  if (segmentPlays.length === 0) return null;
  
  const stageScores = {};
  const stageCounts = {};
  segmentPlays.forEach(p => {
    const stage = p.stage || 'unknown';
    if (!stageScores[stage]) {
      stageScores[stage] = 0;
      stageCounts[stage] = 0;
    }
    stageScores[stage] += p.recommendation_score || 0;
    stageCounts[stage]++;
  });
  
  const stageAvgs = Object.entries(stageScores).map(([stage, total]) => ({
    stage,
    avg: total / stageCounts[stage],
    count: stageCounts[stage]
  })).sort((a, b) => b.avg - a.avg);
  
  const channelScores = {};
  const channelCounts = {};
  segmentPlays.forEach(p => {
    const channel = p.channel || 'unknown';
    if (!channelScores[channel]) {
      channelScores[channel] = 0;
      channelCounts[channel] = 0;
    }
    channelScores[channel] += p.recommendation_score || 0;
    channelCounts[channel]++;
  });
  
  const channelAvgs = Object.entries(channelScores).map(([channel, total]) => ({
    channel,
    avg: total / channelCounts[channel],
    count: channelCounts[channel]
  })).sort((a, b) => b.avg - a.avg);
  
  const topPlays = [...segmentPlays].sort((a, b) => 
    (b.recommendation_score || 0) - (a.recommendation_score || 0)
  ).slice(0, 2);
  
  const evidenceReasons = [];
  topPlays.forEach(p => {
    if (p.evidence && p.evidence.length > 0) {
      evidenceReasons.push(p.evidence[0].detail);
    }
  });
  
  const personalizeCount = segmentPlays.filter(p => 
    p.landing?.type === 'personalize_existing'
  ).length;
  const newPageCount = segmentPlays.length - personalizeCount;
  
  return {
    topStages: stageAvgs.slice(0, 2),
    topChannels: channelAvgs.slice(0, 2),
    evidenceReasons,
    landingBias: personalizeCount > newPageCount ? 'personalize' : 'new_page',
    totalPlays: segmentPlays.length
  };
}

/**
 * Generate a reason string based on segment insights
 */
function generateSegmentReason(insights, goal) {
  if (!insights) return '';
  
  const reasons = [];
  
  if (insights.evidenceReasons.length > 0) {
    const shortReason = insights.evidenceReasons[0];
    if (shortReason.length < 80) {
      reasons.push(shortReason.toLowerCase());
    }
  }
  
  if (goal) {
    const goalLower = goal.toLowerCase();
    if (goalLower.includes('signup') || goalLower.includes('demo') || goalLower.includes('book')) {
      reasons.push('aligns with conversion goal');
    } else if (goalLower.includes('purchase')) {
      reasons.push('fits purchase intent');
    }
  }
  
  if (insights.landingBias === 'personalize') {
    reasons.push('uses existing pages for faster testing');
  }
  
  return reasons.slice(0, 2).join(' and ') || 'based on segment definition and goals';
}

export default function CampaignExplainabilityCard({
  goal,
  offer,
  companyStage,
  budget,
  timeHorizon,
  stance,
  selectedSegments,
  plays,
  productFocus,
  channelsSelected
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMore, setShowMore] = useState(false);
  
  if (!plays || plays.length === 0) return null;
  
  // Compute insights for each segment
  const segmentInsights = {};
  selectedSegments.forEach(seg => {
    const segId = seg.segment_id || seg.id;
    segmentInsights[segId] = {
      name: seg.name,
      insights: computeSegmentInsights(plays, segId)
    };
  });
  
  const isEarlyStage = ['startup', 'seed'].includes(companyStage);
  const isGrowthStage = ['growth', 'post_ipo'].includes(companyStage);
  
  // Count stage distribution
  const stageDistribution = {};
  plays.forEach(p => {
    const stage = p.stage || 'unknown';
    stageDistribution[stage] = (stageDistribution[stage] || 0) + 1;
  });
  
  // Count channel distribution  
  const channelDistribution = {};
  plays.forEach(p => {
    const channel = p.channel || 'unknown';
    channelDistribution[channel] = (channelDistribution[channel] || 0) + 1;
  });
  
  // Collapsed view - Clean neutral bar with amber/yellow icon
  if (!isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="w-full flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100/70 transition-colors text-left"
        data-testid="campaign-explainability-card"
      >
        <div className="p-1.5 bg-amber-200 rounded-lg">
          <Lightbulb className="w-4 h-4 text-amber-700" />
        </div>
        <span className="font-medium text-sm text-slate-700">How Pravik generated these campaigns</span>
        <ChevronDown className="w-4 h-4 text-amber-500 ml-auto" />
      </button>
    );
  }
  
  return (
    <div 
      className="bg-amber-50/50 border border-amber-200 rounded-xl p-4"
      data-testid="campaign-explainability-card"
    >
      {/* Header with collapse button */}
      <button 
        onClick={() => setIsExpanded(false)}
        className="w-full flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity"
      >
        <div className="p-1.5 bg-amber-200 rounded-lg">
          <Lightbulb className="w-4 h-4 text-amber-700" />
        </div>
        <h4 className="font-medium text-sm text-slate-700">How Pravik generated these campaigns</h4>
        <ChevronUp className="w-4 h-4 text-amber-500 ml-auto" />
      </button>
      
      {/* Static explanation bullets - With accent colors for key highlights */}
      <ul className="space-y-3 text-sm text-slate-600">
        {/* Inputs used */}
        <li className="flex items-start gap-2">
          <span className="text-amber-500 mt-0.5 shrink-0">•</span>
          <div>
            <span className="text-slate-700 font-medium">Inputs used: </span>
            <span className="inline-flex flex-wrap gap-1.5">
              {goal && <span className="text-xs bg-purple-100 text-purple-700 border border-purple-200 px-2 py-0.5 rounded font-medium">{goal}</span>}
              {offer && <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded font-medium">{offer}</span>}
              {productFocus && <span className="text-xs text-slate-500">→ {productFocus}</span>}
              {companyStage && (
                <span className="text-xs bg-slate-100 border border-slate-200 px-2 py-0.5 rounded">
                  {companyStage.replace('_', ' ')}
                </span>
              )}
            </span>
          </div>
        </li>
        
        {/* Persona → hypothesis */}
        <li className="flex items-start gap-2">
          <span className="text-amber-500 mt-0.5 shrink-0">•</span>
          <span>
            <span className="text-purple-700 font-medium">Segment → hypothesis: </span>
            For each selected segment, we formed testable hypotheses about what message/offer should convert.
          </span>
        </li>
        
        {/* Hypothesis → plays */}
        <li className="flex items-start gap-2">
          <span className="text-amber-500 mt-0.5 shrink-0">•</span>
          <div>
            <span className="text-blue-700 font-medium">Hypothesis → plays: </span>
            Each hypothesis becomes multiple plays by channel/stage:
            <span className="ml-1 inline-flex flex-wrap gap-1">
              {Object.entries(stageDistribution).map(([stage, count]) => (
                <span key={stage} className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
                  {STAGE_LABELS[stage] || stage} ({count})
                </span>
              ))}
            </span>
          </div>
        </li>
        
        {/* Channel logic */}
        <li className="flex items-start gap-2">
          <span className="text-amber-500 mt-0.5 shrink-0">•</span>
          <div className="flex-1">
            <span className="text-slate-700 font-medium">Channel logic:</span>
            <div className="mt-1.5 grid grid-cols-1 gap-1.5 text-xs text-slate-500">
              {channelDistribution.search_paid && (
                <div className="flex items-center gap-2">
                  <Search className="w-3 h-3 text-emerald-500" />
                  <span><span className="font-medium text-emerald-700">Search</span> → high intent, decision stage</span>
                </div>
              )}
              {channelDistribution.retargeting && (
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-3 h-3 text-orange-500" />
                  <span><span className="font-medium text-orange-700">Retargeting</span> → warm traffic who showed intent</span>
                </div>
              )}
              {channelDistribution.meta_paid && (
                <div className="flex items-center gap-2">
                  <Smartphone className="w-3 h-3 text-blue-500" />
                  <span><span className="font-medium text-blue-700">Meta</span> → creating demand, mid-funnel</span>
                </div>
              )}
              {channelDistribution.email_lifecycle && (
                <div className="flex items-center gap-2">
                  <Mail className="w-3 h-3 text-purple-500" />
                  <span><span className="font-medium text-purple-700">Email</span> → nudging drop-offs (requires lists)</span>
                </div>
              )}
              {channelDistribution.partnerships && (
                <div className="flex items-center gap-2">
                  <Users className="w-3 h-3 text-teal-500" />
                  <span><span className="font-medium text-teal-700">Partners</span> → borrowed trust (Edge unless strong evidence)</span>
                </div>
              )}
            </div>
          </div>
        </li>
        
        {/* Scoring logic */}
        <li className="flex items-start gap-2">
          <span className="text-amber-500 mt-0.5 shrink-0">•</span>
          <span>
            <span className="text-slate-700 font-medium">Scoring: </span>
            Plays ranked by <span className="text-purple-600">goal fit</span>, <span className="text-blue-600">stage fit</span>, <span className="text-emerald-600">product fit</span>, evidence strength, execution friction, and company stage fit.
          </span>
        </li>
        
        {/* Why plays not ad copies */}
        <li className="flex items-start gap-2">
          <span className="text-amber-500 mt-0.5 shrink-0">•</span>
          <span>
            <span className="text-slate-700 font-medium">Why "plays" not ad copies: </span>
            Plays are strategic modules—select the best ones, then generate creative variants.
          </span>
        </li>
      </ul>
      
      {/* Segment-specific insights */}
      {selectedSegments.length > 0 && (
        <div className="mt-4 pt-4 border-t border-amber-200">
          <div className="text-xs font-medium text-amber-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Target className="w-3 h-3" /> Segment Insights
          </div>
          <ul className="space-y-2">
            {Object.entries(segmentInsights).map(([segId, data]) => {
              if (!data.insights) return null;
              const { topStages, topChannels } = data.insights;
              const reason = generateSegmentReason(data.insights, goal);
              
              return (
                <li key={segId} className="flex items-start gap-2 text-sm">
                  <span className="text-amber-500 mt-0.5 shrink-0">→</span>
                  <span>
                    <span className="font-medium text-purple-700">{data.name}</span>
                    <span className="text-slate-500"> — Prioritized </span>
                    {topStages.slice(0, 2).map((s, i) => (
                      <span key={s.stage}>
                        {i > 0 && <span className="text-slate-400">/</span>}
                        <span className="text-xs bg-emerald-100 text-emerald-700 border border-emerald-200 px-1.5 py-0.5 rounded mx-0.5">
                          {STAGE_LABELS[s.stage] || s.stage}
                        </span>
                      </span>
                    ))}
                    <span className="text-slate-500"> + </span>
                    {topChannels.slice(0, 2).map((c, i) => (
                      <span key={c.channel}>
                        {i > 0 && <span className="text-slate-400">/</span>}
                        <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-1.5 py-0.5 rounded mx-0.5">
                          {CHANNEL_LABELS[c.channel] || c.channel}
                        </span>
                      </span>
                    ))}
                    <span className="text-slate-500"> because {reason}.</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      
      {/* Company stage bias */}
      {(isEarlyStage || isGrowthStage) && (
        <div className="mt-4 pt-4 border-t border-amber-200">
          <div className="flex items-start gap-2 text-sm">
            <Building2 className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
            {isEarlyStage ? (
              <span className="text-slate-600">
                <span className="font-medium text-purple-700">Startup bias: </span>
                We prioritized faster-to-test plays (<span className="text-emerald-600">Decision + Retargeting</span>) and lower-lift landing plans (<span className="text-blue-600">personalize existing pages</span>).
              </span>
            ) : (
              <span className="text-slate-600">
                <span className="font-medium text-purple-700">Growth mode: </span>
                We included scalability plays (<span className="text-emerald-600">Awareness + Partners</span>) and broader experimentation options.
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Learn more toggle */}
      <button 
        onClick={() => setShowMore(!showMore)}
        className="mt-4 text-xs text-amber-600 hover:text-amber-700 flex items-center gap-1"
      >
        {showMore ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {showMore ? 'Show less' : 'Learn more about scoring'}
      </button>
      
      {showMore && (
        <div className="mt-3 p-3 bg-white rounded-lg border border-slate-200 text-xs text-slate-600 space-y-1">
          <p><span className="font-medium text-slate-700">Goal alignment (25%):</span> How well does this play serve the primary goal?</p>
          <p><span className="font-medium text-slate-700">Stage fit (20%):</span> Decision/retargeting score higher for conversion goals.</p>
          <p><span className="font-medium text-slate-700">Product fit (15%):</span> How well does the product match the play?</p>
          <p><span className="font-medium text-slate-700">Evidence strength (15%):</span> Number and quality of supporting evidence.</p>
          <p><span className="font-medium text-slate-700">Execution friction (15%):</span> Personalize existing pages &gt; create new pages.</p>
          <p><span className="font-medium text-slate-700">Company stage fit (10%):</span> Startups favor decision/retargeting; growth favors full funnel.</p>
          <p className="pt-1 text-slate-500 italic">Penalties applied for high-severity dependencies and weak evidence.</p>
        </div>
      )}
    </div>
  );
}
